import React, { useMemo, useState } from "react";
import { DropdownToggle, DropdownMenu, Dropdown, Modal, ModalBody, ModalHeader } from "reactstrap";
import { Button, Icon } from "../../../../components/Component";
import { LinkList, LinkItem } from "../../../../components/links/Links";
import UserAvatar from "../../../../components/user/UserAvatar";
import { useTheme, useThemeUpdate } from "../../../provider/Theme";
import { formatApiErrorMessage, getLoggedInAdmin } from "../../../../utils/Utils";
import { useForm } from "react-hook-form";
import { changeAdminPasswordAPI } from "../../../../api/auth";
import useLogout from "../../../../Hooks/Logout";

const User = () => {
  const theme = useTheme();
  const themeUpdate = useThemeUpdate();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prevState) => !prevState);
  const logout = useLogout();
  const [pwdOpen, setPwdOpen] = useState(false);
  const togglePwd = () => setPwdOpen((s) => !s);
  const user = useMemo(() => getLoggedInAdmin(), []);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { current_password: "", new_password: "", confirm_password: "" },
  });

  const onChangePassword = async (vals) => {
    const cur = String(vals.current_password || "").trim();
    const nw = String(vals.new_password || "").trim();
    const confirm = String(vals.confirm_password || "").trim();
    if (nw !== confirm) {
      throw new Error("New password and confirm password must match.");
    }
    const res = await changeAdminPasswordAPI({ current_password: cur, new_password: nw });
    if (res?.data?.status === true) {
      reset();
      setPwdOpen(false);
      // Force re-login for security
      logout("Password changed. Please login again.");
      return;
    }
    throw new Error(formatApiErrorMessage(res?.data) || "Failed to change password");
  };

  return (
    <Dropdown isOpen={open} className="user-dropdown" toggle={toggle}>
      <DropdownToggle
        tag="a"
        href="#toggle"
        className="dropdown-toggle"
        onClick={(ev) => {
          ev.preventDefault();
        }}
      >
        <div className="user-toggle">
          <UserAvatar icon="user-alt" className="sm" />
          <div className="user-info d-none d-md-block">
            <div className="user-status text-capitalize">{user?.role || ""}</div>
            <div className="user-name dropdown-indicator text-capitalize">{user?.username || ""}</div>
          </div>
        </div>
      </DropdownToggle>
      <DropdownMenu end className="dropdown-menu-md dropdown-menu-s1">
        <div className="dropdown-inner user-card-wrap bg-lighter d-none d-md-block">
          <div className="user-card sm">
            <UserAvatar icon="user-alt" className="sm" />
            <div className="user-info">
              <span className="lead-text text-capitalize">{user?.username || ""}</span>
              <span className="sub-text">{user?.email || ""}</span>
            </div>
          </div>
        </div>
        <div className="dropdown-inner">
          {/* <LinkList>
            <LinkItem link="/user-profile-regular" icon="user-alt" onClick={toggle}>
              View Profile
            </LinkItem>
            <LinkItem link="/user-profile-setting" icon="setting-alt" onClick={toggle}>
              Account Setting
            </LinkItem>
            <LinkItem link="/user-profile-activity" icon="activity-alt" onClick={toggle}>
              Login Activity
            </LinkItem>
            <li>
              <a className={`dark-switch ${theme.skin === 'dark' ? 'active' : ''}`} href="#" 
              onClick={(ev) => {
                ev.preventDefault();
                themeUpdate.skin(theme.skin === 'dark' ? 'light' : 'dark');
              }}>
                {theme.skin === 'dark' ? 
                  <><em className="icon ni ni-sun"></em><span>Light Mode</span></> 
                  : 
                  <><em className="icon ni ni-moon"></em><span>Dark Mode</span></>
                }
              </a>
            </li>
          </LinkList> */}
        </div>
        <div className="dropdown-inner">
          <LinkList>
            <li>
              <a
                href="#change-password"
                onClick={(ev) => {
                  ev.preventDefault();
                  toggle();
                  setPwdOpen(true);
                }}
              >
                <Icon name="lock-alt" />
                <span>Change Password</span>
              </a>
            </li>
            <li>
              <a
                href="#signout"
                onClick={(ev) => {
                  ev.preventDefault();
                  toggle();
                  logout("Signed out");
                }}
              >
                <Icon name="signout"></Icon>
                <span>Sign Out</span>
              </a>
            </li>
          </LinkList>
        </div>
      </DropdownMenu>

      <Modal isOpen={pwdOpen} toggle={togglePwd} centered>
        <ModalHeader toggle={togglePwd}>Change Password</ModalHeader>
        <ModalBody>
          <form
            onSubmit={handleSubmit(async (vals) => {
              try {
                await onChangePassword(vals);
              } catch (e) {
                // eslint-disable-next-line no-alert
                alert(e?.message || "Failed to change password");
              }
            })}
          >
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-control"
                {...register("current_password", { required: "Current password is required" })}
              />
              {errors.current_password ? <div className="invalid">{errors.current_password.message}</div> : null}
            </div>
            <div className="form-group mt-3">
              <label className="form-label">New password</label>
              <input
                type="password"
                className="form-control"
                {...register("new_password", {
                  required: "New password is required",
                  minLength: { value: 8, message: "Minimum 8 characters" },
                })}
              />
              {errors.new_password ? <div className="invalid">{errors.new_password.message}</div> : null}
            </div>
            <div className="form-group mt-3">
              <label className="form-label">Confirm new password</label>
              <input
                type="password"
                className="form-control"
                {...register("confirm_password", {
                  required: "Please confirm the new password",
                  validate: (v) => String(v || "") === String(watch("new_password") || "") || "Passwords do not match",
                })}
              />
              {errors.confirm_password ? <div className="invalid">{errors.confirm_password.message}</div> : null}
            </div>

            <div className=" float-end">
              <Button color={"gray"} onClick={togglePwd} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button size={"md"}   color={"primary"} className={'mx-1'} disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : "Update Password"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </Dropdown>
  );
};

export default User;
